/**
 * Die Grund-Pille (Antoine, 20.10.2026, Mockup V5): zwei Fassungen derselben Pille aus
 * `lib/grund-pille.ts` — als Etikett auf der oberen Kartenkante (Kanban) und als einfache Pille in
 * der Liste. Bei „Nachricht vom Gast“ ist sie ein Knopf, der die Antwort-Ansicht öffnet (wie der
 * frühere Chip „Antwort vom Gast“, den sie ablöst).
 */
import { Icon, ZEILEN_AKTION } from "./components";
import { GRUND_PILLE_KLASSEN, type GrundPille } from "../lib/grund-pille";

const INHALT = (pille: GrundPille) => (
  <>
    <Icon name={pille.icon} className="shrink-0 text-[14px]" />
    {pille.label}
  </>
);

/** Testgriff: die Gast-Pille behält den Griff des früheren Chips. */
function testId(pille: GrundPille): string {
  return pille.art === "gast_antwort" ? "karte-gast-antwort" : "grund-pille";
}

interface Props {
  pille: GrundPille;
  /** Nur für „Nachricht vom Gast“: öffnet die Antwort-Ansicht. */
  onKlick?: () => void;
}

/**
 * Etikett OBEN RECHTS auf der Kartenkante: `absolute`, 12 px über der Kante und 12 px vom rechten
 * Rand, 2 px Rand in Kartenfarbe, DECKENDER Hintergrund (Volltonfarbe je Thema).
 */
export function GrundPilleEtikett({ pille, onKlick }: Props) {
  const klassen = `absolute -top-3 right-3 z-[2] inline-flex items-center gap-1 whitespace-nowrap rounded-full border-2 border-surface-container-lowest px-2.5 py-1 text-[11.5px] font-semibold shadow-[0_2px_6px_rgba(45,38,26,0.18)] dark:border-night-card dark:shadow-[0_2px_6px_rgba(0,0,0,0.5)] ${GRUND_PILLE_KLASSEN[pille.tone]}`;
  if (pille.art === "gast_antwort" && onKlick) {
    return (
      <button
        type="button"
        data-testid={testId(pille)}
        data-art={pille.art}
        title={pille.title}
        onClick={(e) => {
          e.stopPropagation();
          onKlick();
        }}
        className={`${klassen} hover:underline`}
      >
        {INHALT(pille)}
      </button>
    );
  }
  return (
    <span data-testid={testId(pille)} data-art={pille.art} title={pille.title} className={klassen}>
      {INHALT(pille)}
    </span>
  );
}

/** Einfache Pille für die Listenansicht (Tabellenzeile und Handy-Karte). */
export function GrundPilleInline({ pille, onKlick }: Props) {
  const klassen = `inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold ${GRUND_PILLE_KLASSEN[pille.tone]}`;
  if (pille.art === "gast_antwort" && onKlick) {
    return (
      <button
        type="button"
        data-testid={testId(pille)}
        data-art={pille.art}
        title={pille.title}
        onClick={(e) => {
          e.stopPropagation();
          onKlick();
        }}
        className={`${klassen} ${ZEILEN_AKTION} hover:underline`}
      >
        {INHALT(pille)}
      </button>
    );
  }
  return (
    <span data-testid={testId(pille)} data-art={pille.art} title={pille.title} className={klassen}>
      {INHALT(pille)}
    </span>
  );
}
